import React, { useState, useEffect, lazy, Suspense, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NotificationFeedOrganism, useApiClientsOrNull, FALLBACK_METASPACE_SYSTEM_NAME } from 'design-system/my-lib';
import Header from '../Header/Header';
import type { RootState } from '@center-pwa/store/store';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../../assets/images/center-icon.png';
import { getSphereById } from '@center-platform/spheres/sphere';
import type { Sphere } from 'types/global';
import './NotificationsPage.scss';

// Lazy load mobile components to reduce initial bundle size for desktop users
const MobileHeader = lazy(() =>
  import('design-system/my-lib').then((m) => ({ default: m.MobileHeader }))
);

const MobileNavBar = lazy(() =>
  import('design-system/my-lib').then((m) => ({ default: m.MobileNavBar }))
);

const MobileNotificationBell = lazy(() =>
  import('design-system/my-lib').then((m) => ({ default: m.MobileNotificationBell }))
);

// Extracted component to avoid nested component definition
interface NotificationBellRendererProps {
  onViewAllClick: () => void;
  isActive?: boolean;
}

const NotificationBellRenderer: React.FC<NotificationBellRendererProps> = ({ onViewAllClick, isActive = false }) => (
  <Suspense fallback={<div className="mobile-component-loading" />}>
    <MobileNotificationBell onViewAllClick={onViewAllClick} isActive={isActive} />
  </Suspense>
);

const NotificationsPage: React.FC = () => {
  const defaultLocale = process.env.REACT_APP_I18N_LOCALE || 'en-US';
  const { userId, userLocale } = useSelector((state: RootState) => ({
    userId: state.userProfile?.userId,
    userLocale: state.userProfile.locale ?? defaultLocale,
  }));
  const navigate = useNavigate();
  const location = useLocation();
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;
  const [selectedSphere, setSelectedSphere] = useState<Sphere | null>(null);

  // Fetch sphere data for mobile header
  useEffect(() => {
    async function fetchSphere() {
      if (!userId || !graphClient) return;

      try {
        const resp = await getSphereById(graphClient, userId, false);
        setSelectedSphere(resp.data.sphere);
      } catch (err) {
        console.error('Error fetching sphere:', err);
      }
    }

    fetchSphere();
  }, [userId, graphClient]);

  const handleSearchChange = useCallback((query: string) => {
    console.log('Search query:', query);
  }, []);

  const handleViewAllNotifications = useCallback(() => {
    navigate('/notifications');
  }, [navigate]);

  const handleAtlasClick = useCallback(() => {
    if (userId) {
      navigate(`/sphere/${userId}/metaspace/${FALLBACK_METASPACE_SYSTEM_NAME}`);
    }
  }, [userId, navigate]);

  // Settings navigation logic (navigate to identity management metaspace)
  const handleSettingsMenuClick = useCallback(() => {
    if (!userId) return;
    navigate(`/sphere/${userId}/metaspace/center_identity_management_metaspace`);
  }, [userId, navigate]);

  // Determine if notifications page is active
  const isNotificationsActive = location.pathname === '/notifications';

  // Memoize the notification bell component to avoid recreating on every render
  const notificationBellComponent = useMemo(
    () => <NotificationBellRenderer onViewAllClick={handleViewAllNotifications} isActive={isNotificationsActive} />,
    [handleViewAllNotifications, isNotificationsActive]
  );

  const navItems = useMemo(() => [
    {
      id: 'home',
      icon: 'Home-icon',
      label: 'Home',
      onClick: () => navigate('/'),
      isActive: location.pathname === '/'
    },
    {
      id: 'atlas',
      icon: 'Atlas-icon',
      label: 'Atlas',
      onClick: handleAtlasClick
    },
    {
      id: 'notifications',
      label: 'Notifications',
      customRender: () => notificationBellComponent,
      isActive: location.pathname === '/notifications'
    },
    {
      id: 'menu',
      icon: 'Settings-icon',
      label: 'Menu',
      onClick: handleSettingsMenuClick
    }
  ], [navigate, location.pathname, handleAtlasClick, handleViewAllNotifications, handleSettingsMenuClick]);

  if (!userId) {
    return (
      <div className="notifications-page-loading">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <Suspense fallback={<div className="mobile-component-loading" />}>
        <MobileHeader
          logoSrc={logo}
          logoAlt="Center Logo"
          onLogoClick={() => navigate('/')}
          onSearchChange={handleSearchChange}
          placeholder="Search Box"
          identityId={selectedSphere?.id}
          sphere={selectedSphere}
          onSphereChange={(newSphereId: string) => {
            if (newSphereId) {
              navigate(`/sphere/${newSphereId}/metaspace/${FALLBACK_METASPACE_SYSTEM_NAME}`);
            }
          }}
        />
      </Suspense>
      <Header sphereId={userId} defaultLanguage={userLocale} />
      <div className="notifications-page">
        <div className="notifications-page-container">
          <NotificationFeedOrganism userId={userId} itemsPerPage={20} />
        </div>
      </div>
      <Suspense fallback={<div className="mobile-component-loading" />}>
        <MobileNavBar items={navItems} position="bottom" />
      </Suspense>
    </>
  );
};

export default NotificationsPage;